import React, { useState, useEffect } from 'react';

/**
 * Professional UnyKorn Protocol Landing Page
 * Institutional-grade design. Revenue-focused. Multi-chain.
 */

const TOKENS = [
  // ── Live on-ledger (all issued by rE85) ──────────────────────────
  { symbol: 'USDT', name: 'USDT (IOU)', class: 'Settlement', desc: '185M circulating · 8 holders · DEX active', color: '#26a17b', logo: '/assets/logos/usdt-logo.svg' },
  { symbol: 'EUR', name: 'EUR (IOU)', class: 'Settlement', desc: '50M circulating · 4 holders', color: '#003399', logo: '/assets/logos/eur-logo.svg' },
  { symbol: 'GOLD', name: 'GOLD (IOU)', class: 'RWA', desc: '20M circulating · 5 holders · DEX active', color: '#ffd700', logo: '/assets/logos/gold-logo.svg' },
  { symbol: 'GBP', name: 'GBP (IOU)', class: 'Settlement', desc: '30M circulating · 4 holders', color: '#1d2951', logo: '/assets/logos/gbp-logo.svg' },
  { symbol: 'USD', name: 'USD (IOU)', class: 'Settlement', desc: '60K circulating · 4 holders', color: '#00cc66', logo: '/assets/logos/xusd-logo.svg' },
  { symbol: 'DONK', name: 'DONK', class: 'Meme', desc: '2M circulating · 3 holders', color: '#9b59b6', logo: '/assets/logos/donk-logo.svg' },
  { symbol: 'DRUNKS', name: 'DRUNKS', class: 'Meme', desc: '50B supply · Community token', color: '#ffaa00', logo: '/assets/logos/drunks-logo.svg' },
  // ── Planned / Roadmap ────────────────────────────────────────────
  { symbol: 'AURG', name: 'Aurum Gram', class: 'RWA', desc: '1g gold · Dealer-attested · Stellar + XRPL', color: '#daa520', logo: '/assets/logos/aurg-logo.svg' },
  { symbol: 'UNYKORN', name: 'UNYKORN', class: 'Governance', desc: 'Platform governance & utility · Planned', color: '#9933ff', logo: '/assets/logos/unykorn-token-logo.svg' },
];

const SERVICES = [
  {
    title: 'Asset Attestation',
    desc: 'Cryptographic proof-of-reserves and inventory attestation on XRPL. Verifiable on-chain.',
    price: 'From $500/mo',
    icon: '✓',
    color: '#10b981'
  },
  {
    title: 'POF-as-a-Service',
    desc: 'Guided wallet setup, trust line configuration, and proof-of-funds workflows.',
    price: 'Custom quote',
    icon: '⚡',
    color: '#3b82f6'
  },
  {
    title: 'DEX Settlement',
    desc: 'XRPL DEX + Stellar DEX settlement infrastructure. Path payments & AMM liquidity.',
    price: 'Request access',
    icon: '💧',
    color: '#8b5cf6'
  },
  {
    title: 'Token Issuance',
    desc: 'Create & manage XRPL issued assets. Full issuer wallet setup with compliance layer.',
    price: 'From $2,500',
    icon: '🪙',
    color: '#f59e0b'
  },
  {
    title: 'Cross-Chain Bridge',
    desc: 'XRPL ↔ Stellar attestation linkage. Unified asset metadata across chains.',
    price: 'Enterprise',
    icon: '🌐',
    color: '#06b6d4'
  },
  {
    title: 'Xaman Integration',
    desc: 'Xaman wallet services, payment flows, and xApp integration for your project.',
    price: 'From $1,000',
    icon: '📱',
    color: '#ec4899'
  },
];

const ENTITIES = [
  { name: 'FTH', full: 'Future Tech Holdings', role: 'Technology' },
  { name: 'MOG', full: 'Men of God LLC', role: 'Ministry' },
  { name: 'OPTKAS1', full: 'OPTKAS1 LLC', role: 'Operations' },
  { name: 'XXXIII', full: 'XXXIII LLC', role: 'Strategic' },
  { name: 'KBURNS', full: 'K Burns Holdings', role: 'Reserve' },
];

const STATS = [
  { label: 'Live Tokens', value: '9' },
  { label: 'Chains Active', value: '2' },
  { label: 'Entities', value: '5' },
  { label: 'Wallets', value: '20+' },
];

const font = "'SF Pro Display', -apple-system, BlinkMacSystemFont, system-ui, sans-serif";

interface ProfessionalLandingProps {
  onNavigate: (page: string) => void;
}

const ProfessionalLanding: React.FC<ProfessionalLandingProps> = ({ onNavigate }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [activeTokenIdx, setActiveTokenIdx] = useState(0);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Auto-rotate token showcase
  useEffect(() => {
    const iv = setInterval(() => setActiveTokenIdx(i => (i + 1) % TOKENS.length), 3000);
    return () => clearInterval(iv);
  }, []);

  const sectionStyle: React.CSSProperties = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: isMobile ? '60px 20px' : '100px 48px',
  };

  return (
    <div style={{ minHeight: '100vh', background: '#000', color: '#fff', fontFamily: font }}>
      {/* ─── NAV ──────────────────────────── */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        padding: isMobile ? '12px 20px' : '16px 48px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }} onClick={() => onNavigate('landing')}>
          <img src="/assets/logos/unykorn-logo.svg" alt="UnyKorn" style={{ height: '32px', width: '32px' }} />
          <span style={{ fontSize: '18px', fontWeight: 600, letterSpacing: '0.04em', color: '#e0e0e0' }}>UNYKORN</span>
          <span style={{ fontSize: '11px', color: '#6b7280', letterSpacing: '0.1em', marginLeft: '-6px' }}>7777</span>
        </div>
        {!isMobile && (
          <div style={{ display: 'flex', gap: '28px', fontSize: '13px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            {[
              { label: 'Assets', page: 'tokens' },
              { label: 'Services', page: 'services' },
              { label: 'DEX', page: 'dex' },
              { label: 'Stellar', page: 'stellar' },
              { label: 'Verify', page: 'pof' },
              { label: 'Ecosystem', page: 'ecosystem' },
            ].map(nav => (
              <span key={nav.page} onClick={() => onNavigate(nav.page)} style={{ color: '#9ca3af', cursor: 'pointer', transition: 'color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.color = '#fff'}
                onMouseLeave={e => e.currentTarget.style.color = '#9ca3af'}>
                {nav.label}
              </span>
            ))}
          </div>
        )}
        <button onClick={() => onNavigate('xaman-services')} style={{
          padding: '8px 20px', fontSize: '12px', fontWeight: 600, letterSpacing: '0.1em',
          background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff',
          border: 'none', borderRadius: '6px', cursor: 'pointer', textTransform: 'uppercase'
        }}>
          Connect
        </button>
      </nav>

      {/* ─── HERO ─────────────────────────── */}
      <section style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        padding: isMobile ? '120px 20px 60px' : '140px 48px 80px',
        position: 'relative', overflow: 'hidden'
      }}>
        {/* Background glow */}
        <div style={{
          position: 'absolute', top: '30%', left: '50%', transform: 'translate(-50%, -50%)',
          width: '900px', height: '900px',
          background: 'radial-gradient(circle, rgba(59,130,246,0.06) 0%, transparent 70%)',
          filter: 'blur(80px)', pointerEvents: 'none'
        }} />

        <div style={{ position: 'relative', zIndex: 1, textAlign: 'center', maxWidth: '1000px' }}>
          {/* Protocol logo */}
          <img src="/assets/logos/unykorn-logo.svg" alt="UnyKorn Protocol" style={{
            width: isMobile ? '80px' : '120px', height: isMobile ? '80px' : '120px', marginBottom: '32px'
          }} />

          <h1 style={{
            fontSize: isMobile ? '48px' : '96px', fontWeight: 200, letterSpacing: '0.05em', lineHeight: 1.1,
            background: 'linear-gradient(180deg, #fff 0%, #c0c0c0 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', marginBottom: '24px'
          }}>
            UNYKORN
          </h1>

          <p style={{
            fontSize: isMobile ? '16px' : '22px', fontWeight: 300, color: '#9ca3af', letterSpacing: '0.08em',
            textTransform: 'uppercase', maxWidth: '700px', margin: '0 auto 16px', lineHeight: 1.5
          }}>
            Multi-Chain Asset Attestation Protocol
          </p>

          <p style={{
            fontSize: isMobile ? '14px' : '16px', color: '#6b7280', maxWidth: '600px',
            margin: '0 auto 48px', lineHeight: 1.7, fontWeight: 300
          }}>
            Cryptographic proof-of-reserves. Weight-referenced RWAs. Settlement tokens. 
            Institutional infrastructure on XRPL &amp; Stellar.
          </p>

          {/* Stats strip */}
          <div style={{
            display: 'flex', justifyContent: 'center', gap: isMobile ? '24px' : '60px',
            flexWrap: 'wrap', marginBottom: '60px'
          }}>
            {STATS.map(s => (
              <div key={s.label} style={{ textAlign: 'center' }}>
                <div style={{ fontSize: isMobile ? '28px' : '40px', fontWeight: 200, color: '#fff' }}>{s.value}</div>
                <div style={{ fontSize: '11px', color: '#6b7280', letterSpacing: '0.12em', textTransform: 'uppercase', marginTop: '4px' }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => onNavigate('services')} style={{
              padding: '16px 40px', fontSize: '14px', fontWeight: 600, letterSpacing: '0.12em',
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff',
              border: 'none', borderRadius: '8px', cursor: 'pointer', textTransform: 'uppercase',
              boxShadow: '0 8px 32px rgba(59,130,246,0.3)'
            }}>
              View Services
            </button>
            <button onClick={() => onNavigate('pof')} style={{
              padding: '16px 40px', fontSize: '14px', fontWeight: 600, letterSpacing: '0.12em',
              background: 'transparent', color: '#9ca3af',
              border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', cursor: 'pointer', textTransform: 'uppercase'
            }}>
              Verify Assets
            </button>
          </div>

          {/* Chain badges */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '40px' }}>
            {['XRPL Mainnet', 'Stellar Public'].map(chain => (
              <span key={chain} style={{
                padding: '6px 16px', fontSize: '11px', color: '#6b7280',
                border: '1px solid rgba(255,255,255,0.08)', borderRadius: '20px',
                letterSpacing: '0.1em', textTransform: 'uppercase'
              }}>
                {chain}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TOKEN SHOWCASE ───────────────── */}
      <section style={{ ...sectionStyle, background: '#000' }}>
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2 style={{ fontSize: isMobile ? '28px' : '40px', fontWeight: 200, letterSpacing: '0.04em', marginBottom: '16px' }}>
            Token Ecosystem
          </h2>
          <p style={{ color: '#6b7280', fontSize: '15px', maxWidth: '600px', margin: '0 auto' }}>
            RWAs, settlement tokens, governance, and community assets — all with proper metadata and attestation.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '16px'
        }}>
          {TOKENS.map((token, idx) => (
            <div key={token.symbol} onClick={() => setActiveTokenIdx(idx)} style={{
              padding: '24px',
              background: idx === activeTokenIdx ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.02)',
              border: `1px solid ${idx === activeTokenIdx ? token.color + '40' : 'rgba(255,255,255,0.06)'}`,
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              display: 'flex', alignItems: 'center', gap: '16px'
            }}>
              <img src={token.logo} alt={token.symbol} style={{ width: '48px', height: '48px', borderRadius: '50%' }} />
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontSize: '16px', fontWeight: 600, color: token.color }}>{token.symbol}</span>
                  <span style={{
                    fontSize: '9px', padding: '2px 8px', borderRadius: '10px',
                    background: token.color + '15', color: token.color,
                    letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600
                  }}>
                    {token.class}
                  </span>
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>{token.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── SERVICES ─────────────────────── */}
      <section style={{
        ...sectionStyle,
        background: 'linear-gradient(180deg, rgba(255,255,255,0.02) 0%, transparent 100%)',
        borderTop: '1px solid rgba(255,255,255,0.04)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2 style={{ fontSize: isMobile ? '28px' : '40px', fontWeight: 200, letterSpacing: '0.04em', marginBottom: '16px' }}>
            Revenue Services
          </h2>
          <p style={{ color: '#6b7280', fontSize: '15px', maxWidth: '600px', margin: '0 auto' }}>
            Institutional-grade infrastructure as a service. Generate revenue through attestation, settlement, and issuance.
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: '20px'
        }}>
          {SERVICES.map(svc => (
            <div key={svc.title} style={{
              padding: '32px',
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '16px',
              transition: 'all 0.3s ease',
              cursor: 'pointer'
            }}
              onClick={() => onNavigate('services')}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = svc.color + '40';
                e.currentTarget.style.transform = 'translateY(-4px)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <div style={{
                width: '48px', height: '48px', borderRadius: '12px',
                background: svc.color + '15', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '22px', marginBottom: '20px'
              }}>
                {svc.icon}
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '8px', color: '#e5e7eb' }}>
                {svc.title}
              </h3>
              <p style={{ fontSize: '13px', color: '#6b7280', lineHeight: 1.6, marginBottom: '16px' }}>
                {svc.desc}
              </p>
              <span style={{ fontSize: '12px', color: svc.color, fontWeight: 600, letterSpacing: '0.05em' }}>
                {svc.price}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ─── MULTI-CHAIN ARCHITECTURE ─────── */}
      <section style={{ ...sectionStyle, borderTop: '1px solid rgba(255,255,255,0.04)' }}>
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h2 style={{ fontSize: isMobile ? '28px' : '40px', fontWeight: 200, letterSpacing: '0.04em', marginBottom: '16px' }}>
            Multi-Chain Architecture
          </h2>
          <p style={{ color: '#6b7280', fontSize: '15px', maxWidth: '600px', margin: '0 auto' }}>
            XRPL for attestation and settlement. Stellar for circulation and DEX trading. Cross-chain metadata linkage.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px' }}>
          {/* XRPL Card */}
          <div style={{
            padding: '40px', borderRadius: '20px',
            background: 'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(59,130,246,0.02) 100%)',
            border: '1px solid rgba(59,130,246,0.2)'
          }}>
            <div style={{ fontSize: '12px', color: '#3b82f6', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 600, marginBottom: '12px' }}>
              XRPL MAINNET
            </div>
            <h3 style={{ fontSize: '24px', fontWeight: 300, marginBottom: '16px' }}>Attestation Layer</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '14px', color: '#9ca3af' }}>
              {['Asset issuance & trust lines', 'Proof-of-reserves attestation', 'DEX settlement & path payments', 'AMM liquidity pools', 'Xaman wallet integration'].map(
                item => <li key={item} style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>→ {item}</li>
              )}
            </ul>
            <div style={{ marginTop: '20px', fontSize: '11px', color: '#4b5563' }}>
              Gateway: rpP12...XnW1 · Token Issuer: rE85p...Dqm
            </div>
          </div>

          {/* Stellar Card */}
          <div style={{
            padding: '40px', borderRadius: '20px',
            background: 'linear-gradient(135deg, rgba(139,92,246,0.08) 0%, rgba(139,92,246,0.02) 100%)',
            border: '1px solid rgba(139,92,246,0.2)'
          }}>
            <div style={{ fontSize: '12px', color: '#8b5cf6', letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 600, marginBottom: '12px' }}>
              STELLAR PUBLIC
            </div>
            <h3 style={{ fontSize: '24px', fontWeight: 300, marginBottom: '16px' }}>Circulation Layer</h3>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, fontSize: '14px', color: '#9ca3af' }}>
              {['AURG/XLM DEX trading', 'Cross-chain attestation linkage', 'SEP-1 stellar.toml metadata', 'Market maker operations', 'Arbitrage monitoring'].map(
                item => <li key={item} style={{ padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>→ {item}</li>
              )}
            </ul>
            <div style={{ marginTop: '20px', fontSize: '11px', color: '#4b5563' }}>
              Issuer: GDFPC...SU7A · home_domain: unykorn7777.com
            </div>
          </div>
        </div>
      </section>

      {/* ─── ENTITY NETWORK ───────────────── */}
      <section style={{
        ...sectionStyle,
        background: 'linear-gradient(180deg, transparent 0%, rgba(255,255,255,0.02) 100%)',
        borderTop: '1px solid rgba(255,255,255,0.04)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h2 style={{ fontSize: isMobile ? '24px' : '32px', fontWeight: 200, letterSpacing: '0.04em', marginBottom: '12px' }}>
            Entity Network
          </h2>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>5 Wyoming entities · 20+ operational wallets</p>
        </div>

        <div style={{
          display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap'
        }}>
          {ENTITIES.map(ent => (
            <div key={ent.name} style={{
              padding: '20px 28px', borderRadius: '12px',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
              textAlign: 'center', minWidth: '140px'
            }}>
              <div style={{ fontSize: '20px', fontWeight: 600, letterSpacing: '0.05em', marginBottom: '4px' }}>{ent.name}</div>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: '4px' }}>{ent.full}</div>
              <div style={{ fontSize: '10px', color: '#4b5563', letterSpacing: '0.1em', textTransform: 'uppercase' }}>{ent.role}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── CTA SECTION ──────────────────── */}
      <section style={{
        maxWidth: '1200px', margin: '0 auto',
        padding: isMobile ? '80px 20px' : '120px 48px',
        textAlign: 'center'
      }}>
        <h2 style={{
          fontSize: isMobile ? '32px' : '48px', fontWeight: 200, letterSpacing: '0.04em', marginBottom: '20px',
          background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
        }}>
          Ready to Build?
        </h2>
        <p style={{ fontSize: '16px', color: '#6b7280', maxWidth: '500px', margin: '0 auto 40px', lineHeight: 1.7 }}>
          Whether you need asset attestation, DEX settlement, or token issuance — 
          we have the infrastructure to support institutional-grade operations.
        </p>
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button onClick={() => onNavigate('services')} style={{
            padding: '16px 48px', fontSize: '14px', fontWeight: 600, letterSpacing: '0.12em',
            background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff',
            border: 'none', borderRadius: '8px', cursor: 'pointer', textTransform: 'uppercase',
            boxShadow: '0 8px 32px rgba(59,130,246,0.3)'
          }}>
            Get Started
          </button>
          <button onClick={() => onNavigate('dex')} style={{
            padding: '16px 48px', fontSize: '14px', fontWeight: 600, letterSpacing: '0.12em',
            background: 'transparent', color: '#9ca3af',
            border: '1px solid rgba(255,255,255,0.15)', borderRadius: '8px', cursor: 'pointer', textTransform: 'uppercase'
          }}>
            Trade on DEX
          </button>
        </div>
      </section>

      {/* ─── FOOTER ───────────────────────── */}
      <footer style={{
        borderTop: '1px solid rgba(255,255,255,0.06)',
        padding: isMobile ? '40px 20px' : '60px 48px',
        maxWidth: '1200px', margin: '0 auto'
      }}>
        <div style={{
          display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
          gap: '40px', marginBottom: '40px'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <img src="/assets/logos/unykorn-logo.svg" alt="UnyKorn" style={{ height: '28px' }} />
              <span style={{ fontSize: '16px', fontWeight: 600, color: '#e0e0e0' }}>UNYKORN</span>
            </div>
            <p style={{ fontSize: '12px', color: '#4b5563', lineHeight: 1.6 }}>
              UNYKORN 7777 INC<br />
              30 N Gould St Ste R<br />
              Sheridan, WY 82801
            </p>
          </div>
          <div>
            <h4 style={{ fontSize: '12px', color: '#6b7280', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>Platform</h4>
            {['Assets', 'Services', 'DEX', 'Stellar DEX', 'Verification'].map(link => (
              <div key={link} style={{ fontSize: '13px', color: '#4b5563', padding: '4px 0', cursor: 'pointer' }}
                onClick={() => onNavigate(link.toLowerCase().replace(' ', '-'))}>{link}</div>
            ))}
          </div>
          <div>
            <h4 style={{ fontSize: '12px', color: '#6b7280', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>Legal</h4>
            {[
              { label: 'Terms of Service', page: 'terms' },
              { label: 'Privacy Policy', page: 'privacy' },
              { label: 'Disclaimers', page: 'legal' },
            ].map(link => (
              <div key={link.page} style={{ fontSize: '13px', color: '#4b5563', padding: '4px 0', cursor: 'pointer' }}
                onClick={() => onNavigate(link.page)}>{link.label}</div>
            ))}
          </div>
          <div>
            <h4 style={{ fontSize: '12px', color: '#6b7280', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '16px' }}>Chains</h4>
            <div style={{ fontSize: '12px', color: '#4b5563', lineHeight: 1.8 }}>
              <div>XRPL Mainnet</div>
              <div>Stellar Public Network</div>
              <div style={{ marginTop: '12px', fontSize: '11px' }}>
                <a href="https://xrplcluster.com" target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6', textDecoration: 'none' }}>
                  xrplcluster.com
                </a>
              </div>
              <div style={{ fontSize: '11px' }}>
                <a href="https://horizon.stellar.org" target="_blank" rel="noopener noreferrer" style={{ color: '#8b5cf6', textDecoration: 'none' }}>
                  horizon.stellar.org
                </a>
              </div>
            </div>
          </div>
        </div>

        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.04)', paddingTop: '24px',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px'
        }}>
          <span style={{ fontSize: '11px', color: '#4b5563' }}>
            © 2026 UNYKORN 7777 INC. Not a registered investment advisor. Not FDIC insured.
          </span>
          <span style={{ fontSize: '11px', color: '#4b5563' }}>
            Built on XRPL &amp; Stellar
          </span>
        </div>
      </footer>
    </div>
  );
};

export default ProfessionalLanding;
